import type { DiagramSpec } from "../types"

/**
 * VidMax Pillar: UGC Workspace — frontend → routers → services → jobs → providers.
 * Shows the full request path for "user generates a UGC asset/video".
 */
export const vidmaxPillarUgc: DiagramSpec = {
  id: "vidmax-pillar-ugc",
  name: "Pillar: UGC Workspace",
  description: "End-to-end flow for the UGC Workspace surface — components, routers, services, jobs, providers, and DB",
  layers: [
    { id: "frontend", name: "Frontend Components", color: "#6366f1" },
    { id: "routers", name: "tRPC Routers", color: "#f59e0b" },
    { id: "services", name: "Services", color: "#10b981" },
    { id: "jobs", name: "Inngest Jobs", color: "#06b6d4" },
    { id: "data", name: "DB Tables", color: "#a855f7" },
    { id: "providers", name: "Providers", color: "#ec4899" },
  ],
  nodes: [
    // Frontend
    {
      id: "ugc-page",
      label: "UGCWorkspacePage",
      layer: "frontend",
      rank: 0,
      type: "Page",
      description: "Top-level route. Composes Workspace + ProjectRail.",
      fields: { file: "apps/vidmax-vite/src/components/UGCWorkspace/UGCWorkspacePage.tsx" },
    },
    {
      id: "workspace",
      label: "Workspace",
      layer: "frontend",
      rank: 0,
      type: "Component",
      description: "Layout shell. Hosts canvas + side panels.",
      fields: { file: "apps/vidmax-vite/src/components/UGCWorkspace/Workspace.tsx" },
    },
    {
      id: "asset-canvas",
      label: "AssetCanvas",
      layer: "frontend",
      rank: 0,
      type: "Component",
      description: "Image/video preview. Triggers regenerate via mutations.",
      fields: { file: "AssetCanvas.tsx" },
    },
    {
      id: "template-gallery",
      label: "TemplateGallery",
      layer: "frontend",
      rank: 0,
      type: "Component",
      description: "Browse + apply UGC templates.",
      fields: { file: "TemplateGallery.tsx" },
    },
    {
      id: "avatar-picker",
      label: "AvatarPicker",
      layer: "frontend",
      rank: 0,
      type: "Component",
      description: "Pick from avatar library; powers lipsync.",
      fields: { file: "AvatarPicker.tsx" },
    },
    {
      id: "voice-picker",
      label: "VoicePicker",
      layer: "frontend",
      rank: 0,
      type: "Component",
      description: "Pick TTS voice.",
      fields: { file: "VoicePicker.tsx" },
    },

    // Routers
    {
      id: "r-ugcVideo",
      label: "ugcVideo",
      layer: "routers",
      rank: 1,
      type: "tRPC",
      description: "create / list / getById / regenerate UGC videos.",
      fields: { file: "apps/vidmax-api/src/router/ugcVideo.ts", tests: "ugcVideo.router.test.ts" },
    },
    {
      id: "r-ugcTemplates",
      label: "ugcTemplates",
      layer: "routers",
      rank: 1,
      type: "tRPC",
      description: "Template CRUD + listing.",
      fields: { file: "ugcTemplates.ts" },
    },
    {
      id: "r-images",
      label: "images",
      layer: "routers",
      rank: 1,
      type: "tRPC",
      description: "Generate / transform / list user images.",
      fields: { file: "images.ts" },
    },
    {
      id: "r-avatar",
      label: "avatar / avatarLibrary",
      layer: "routers",
      rank: 1,
      type: "tRPC",
      description: "Avatar library + custom avatar creation.",
      fields: { file: "avatar.ts, avatarLibrary.ts" },
    },
    {
      id: "r-voices",
      label: "voices",
      layer: "routers",
      rank: 1,
      type: "tRPC",
      description: "Voice catalog + cloning.",
      fields: { file: "voices.ts" },
    },

    // Services
    {
      id: "s-userUgcVideo",
      label: "userUgcVideo",
      layer: "services",
      rank: 2,
      type: "Service",
      description: "Orchestrates UGC video creation: validate template → debit credits → enqueue job.",
      fields: { file: "apps/vidmax-api/src/service/userUgcVideo.ts" },
    },
    {
      id: "s-userUgc",
      label: "userUgc",
      layer: "services",
      rank: 2,
      type: "Service",
      description: "Asset-level UGC primitive. Persists UserUgc rows.",
      fields: { file: "userUgc.ts" },
    },
    {
      id: "s-image",
      label: "image",
      layer: "services",
      rank: 2,
      type: "Service",
      description: "Image generation orchestration. Multi-provider routing.",
      fields: { file: "image.ts" },
    },
    {
      id: "s-voice",
      label: "voice",
      layer: "services",
      rank: 2,
      type: "Service",
      description: "TTS + voice cloning service.",
      fields: { file: "voice.ts" },
    },
    {
      id: "s-credit",
      label: "credit (debit)",
      layer: "services",
      rank: 2,
      type: "Gate",
      description: "Validates balance + debits before job enqueue.",
      fields: { file: "credit.ts, pricing.ts" },
    },

    // Jobs
    {
      id: "j-ugcGen",
      label: "handleUgcGeneration",
      layer: "jobs",
      rank: 3,
      type: "Inngest",
      description: "End-to-end: image → animate → TTS → lipsync → finalize.",
      fields: { file: "apps/vidmax-api/src/inngest/handlers/handleUgcGeneration.ts" },
    },
    {
      id: "j-imageGen",
      label: "handleImageGeneration",
      layer: "jobs",
      rank: 3,
      type: "Inngest",
      description: "Generate base image from prompt + provider.",
      fields: { file: "handleImageGeneration.ts" },
    },
    {
      id: "j-tts",
      label: "handleTTSGeneration",
      layer: "jobs",
      rank: 3,
      type: "Inngest",
      description: "Synthesize speech audio from text.",
      fields: { file: "handleTTSGeneration.ts" },
    },
    {
      id: "j-lipsync",
      label: "handleLipsyncVideo",
      layer: "jobs",
      rank: 3,
      type: "Inngest",
      description: "Drive avatar lipsync from audio.",
      fields: { file: "handleLipsyncVideo.ts" },
    },

    // Data
    {
      id: "d-userUgcVideo",
      label: "UserUgcVideo",
      layer: "data",
      rank: 4,
      type: "Table",
      description: "Per-user UGC video record (status, urls, credit cost).",
      fields: { schema: "packages/ai-db/prisma/vidmax.prisma" },
    },
    {
      id: "d-userUgc",
      label: "UserUgc",
      layer: "data",
      rank: 4,
      type: "Table",
      description: "Asset primitive (image/video/audio piece).",
      fields: { schema: "vidmax.prisma" },
    },
    {
      id: "d-ugcTemplate",
      label: "UgcTemplate",
      layer: "data",
      rank: 4,
      type: "Table",
      description: "Template definitions + prompt scaffolds.",
      fields: { schema: "vidmax.prisma" },
    },
    {
      id: "d-assetJob",
      label: "AssetJob",
      layer: "data",
      rank: 4,
      type: "Table",
      description: "Provider job state machine (queued → processing → succeeded/failed).",
      fields: { schema: "vidmax.prisma" },
    },

    // Providers
    {
      id: "p-image",
      label: "Image (Nano Banana, Replicate)",
      layer: "providers",
      rank: 5,
      type: "External",
      description: "Image gen providers.",
    },
    {
      id: "p-tts",
      label: "TTS (ElevenLabs, Polly)",
      layer: "providers",
      rank: 5,
      type: "External",
      description: "Speech synthesis.",
    },
    {
      id: "p-lipsync",
      label: "Lipsync (Kie.ai, Replicate)",
      layer: "providers",
      rank: 5,
      type: "External",
      description: "Audio-driven avatar animation.",
    },
  ],
  edges: [
    // Frontend composition
    { id: "e-page-ws", source: "ugc-page", target: "workspace" },
    { id: "e-ws-canvas", source: "workspace", target: "asset-canvas" },
    { id: "e-ws-tpl", source: "workspace", target: "template-gallery" },
    { id: "e-ws-avatar", source: "workspace", target: "avatar-picker" },
    { id: "e-ws-voice", source: "workspace", target: "voice-picker" },

    // Frontend → Routers
    { id: "e-canvas-ugcVideo", source: "asset-canvas", target: "r-ugcVideo" },
    { id: "e-canvas-images", source: "asset-canvas", target: "r-images" },
    { id: "e-tpl-r", source: "template-gallery", target: "r-ugcTemplates" },
    { id: "e-avatar-r", source: "avatar-picker", target: "r-avatar" },
    { id: "e-voice-r", source: "voice-picker", target: "r-voices" },

    // Routers → Services
    { id: "e-r-ugcVideo-s", source: "r-ugcVideo", target: "s-userUgcVideo" },
    { id: "e-r-images-s", source: "r-images", target: "s-image" },
    { id: "e-r-tpl-s", source: "r-ugcTemplates", target: "s-userUgc" },
    { id: "e-r-voices-s", source: "r-voices", target: "s-voice" },

    // Services → Credits gate (every paid action passes through)
    { id: "e-s-ugcVideo-credit", source: "s-userUgcVideo", target: "s-credit" },
    { id: "e-s-image-credit", source: "s-image", target: "s-credit" },
    { id: "e-s-voice-credit", source: "s-voice", target: "s-credit" },

    // Services → Jobs
    { id: "e-s-ugcVideo-j", source: "s-userUgcVideo", target: "j-ugcGen" },
    { id: "e-s-image-j", source: "s-image", target: "j-imageGen" },
    { id: "e-s-voice-j", source: "s-voice", target: "j-tts" },

    // Job orchestration (handleUgcGeneration fans out)
    { id: "e-j-ugc-image", source: "j-ugcGen", target: "j-imageGen", style: "dashed" },
    { id: "e-j-ugc-tts", source: "j-ugcGen", target: "j-tts", style: "dashed" },
    { id: "e-j-ugc-lipsync", source: "j-ugcGen", target: "j-lipsync", style: "dashed" },

    // Jobs → Providers
    { id: "e-j-image-p", source: "j-imageGen", target: "p-image" },
    { id: "e-j-tts-p", source: "j-tts", target: "p-tts" },
    { id: "e-j-lipsync-p", source: "j-lipsync", target: "p-lipsync" },

    // Jobs → Data
    { id: "e-j-image-d", source: "j-imageGen", target: "d-assetJob" },
    { id: "e-j-ugc-d", source: "j-ugcGen", target: "d-userUgcVideo" },
    { id: "e-s-userUgc-d", source: "s-userUgc", target: "d-userUgc" },
    { id: "e-r-tpl-d", source: "r-ugcTemplates", target: "d-ugcTemplate" },
  ],
  views: [
    { id: "full", name: "Full UGC Pillar", layout: "TB" },
    { id: "request-path", name: "Request Path", layout: "TB", layers: ["frontend", "routers", "services", "jobs"] },
    { id: "data-only", name: "Data Layer", layout: "TB", layers: ["services", "jobs", "data"] },
  ],
  defaultView: "full",
  preferences: {
    nodeWidth: 190,
    rankSpacing: 110,
    nodeGap: 30,
    showLayerBands: true,
    edgeStyle: "smoothstep",
  },
}
